/**
 * The frame loop (plan §2.4, spec §4.2). Fixed 60 Hz sim steps with carried-over time, pause on
 * visibility, blur, pointer-lock loss and gamepad disconnect, and the frame order:
 * input → pause/perf/dev edges → (not paused:) FA and camera edges → every `beforeTicks` (M2) →
 * combat edges (weapon select, cycle, target) → ticks (player control incl. fire → Control, dev pre-tick,
 * stepSim, afterTick; none while held) → interpolation → camera rig → ship views → dust → features.frame →
 * lib.update, env.update → scene.updateMatrixWorld (the frame's one walk, PERF L-M1) → the view LOD frame (SC-6a1) →
 * post.render → features.overlay.
 *
 * Holds (M2 plan §2.4, `GameContext.holds`): while any hold reason is present (docked, loading) the loop runs no
 * ticks and no `afterTick` and reports `FrameInfo.held`, without the pause menu. When a hold begins it releases the
 * pointer lock once; losing the lock while held does not pause, and resuming from the pause menu does not request
 * it. When the last hold ends (not paused) it requests the lock again; a refusal pauses with 'pointerLock'. The
 * Esc/P pause edge still opens the pause menu while held. When the last input was the pad (a button press after the
 * last key or mouse press) the hold-end request is skipped: a pad press is no user activation, so the request would
 * be refused and pause; flight goes on stick-driven without the lock (dashboard spec §1.5, card D1).
 *
 * The loop shows no UI of its own apart from the start overlay: pause UI belongs to the menu feature.
 * Every per-frame path is allocation-free (D-014).
 *
 * F3 (PERF PF-1): every frame records its JS time and its split (sim ticks, features = `host.frame` +
 * `host.overlay`, render submit = `ctx.post.render()`), and, while F3 is visible, the preset, backend and strategic
 * mode (`strategicBoot()?.client.mode`, read-only).
 *
 * Modal UI (M3 R1, plan §2.5, D-TBD 11): while `ctx.ui.modal` > 0 (a map or panel is open over the running sim)
 * the player's control is zeroed, the flight edge actions (FA, camera, weapon select, target) are dropped,
 * pointer-lock loss does not pause and a resume does not take the lock. Esc/P goes to `ctx.ui.escape` first.
 * `ctx.controlFilters` run on the player's control every tick.
 */
import { Quaternion } from 'three/webgpu'
import { consumeTicks, createStepper, interpolationAlpha, resetStepper } from '../core/loop'
import { logger } from '../core/logger'
import type { Vec3 } from '../core/math'
import { recordFrame, togglePerf, updatePerf, type PerfOverlay } from '../dev/perfOverlay'
import type { SimControl } from '../dev/simControl'
import { drainPressed, endFrame, pollGamepad } from '../input/devices'
import { recenterJoystick } from '../input/joystick'
import { createPlayerControl, readPlayerControl } from '../input/playerInput'
import { updateCameraRig } from '../render/cameraRig'
import type { Dust } from '../render/dust'
import { backendName } from '../render/renderer'
import { updateShipViews } from '../render/shipViews'
import { beginViewLodFrame } from '../render/viewLod'
import { pressTurretHoldFire } from '../combat/turrets'
import { stepSim } from '../sim'
import { strategicBoot } from '../strategic-link/service'
import { Life, Pos64, PrevPos64, PrevRot, Rot, Ship } from '../world/components'
import { applyCombatEdges, createCombatEdges, drainCombatEdges, writePlayerControl, type FireHold } from './combatInput'
import { pixelRatioFor, type AppConfig } from './config'
import type { FrameInfo, GameContext, GameFeature } from './feature'
import type { FeatureHost } from './features'
import { createPauseControl, type PauseControl } from './pause'
import { applyControlFilters, consumeEscape } from './ui'

export interface LoopOptions {
  ctx: GameContext
  host: FeatureHost
  config: AppConfig
  dust: Dust
  perf: PerfOverlay
  /** F2 dev panel (dev builds only). */
  devPanel: { toggle(): void } | null
  /** False in test builds: Playwright cannot take pointer lock. */
  requirePointerLock: boolean
  /** Forced fire from the test-build debug API (always present; idle unless `holdFire` was called). */
  hold: FireHold
  /** Dev/test sim control (invulnerability runs before each tick); null in production builds. */
  simControl: SimControl | null
  /** Live particle count from the fx feature, for the F3 overlay (null when that feature is absent). */
  particleStats: { readonly particles: number } | null
}

export interface GameLoop {
  readonly pause: PauseControl
  /** Starts the render loop (the game itself waits behind the start overlay until `pause.start()`). */
  run(): Promise<void>
  stop(): void
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function createGameLoop(opts: LoopOptions): GameLoop {
  const { ctx, host, config, dust, perf, devPanel, hold, simControl, particleStats } = opts
  const { sim, input, rig, camera, canvas, views, scene } = ctx
  // PERF L-M1: the loop owns the main scene's world-matrix update. three r186 walks the whole graph in every scene
  // render while auto update is on (~7 a frame on High: 4 shadow levels, the AO prepass, the scene pass); the frame
  // below walks it once, after the last movers. Nothing moves a scene object between that walk and the passes.
  scene.matrixWorldAutoUpdate = false
  const bindings = ctx.data.bindings
  const stepper = createStepper()
  const hasLock = (): boolean => document.pointerLockElement === canvas
  const isHeld = (): boolean => ctx.holds.size > 0

  // Requests the pointer lock; a refusal (e.g. Chrome's re-lock cooldown, no user activation) pauses.
  const requestLock = (): void => {
    Promise.resolve(canvas.requestPointerLock()).catch((err: unknown) => {
      logger.warn('Pointer lock refused', err)
      pause.pause('pointerLock')
    })
  }

  const pause = createPauseControl(ctx.bus, {
    onPause(): void {
      resetStepper(stepper)
      if (opts.requirePointerLock && hasLock()) document.exitPointerLock()
    },
    onResume(): void {
      resetStepper(stepper)
      // Synchronous inside the resuming gesture. While held (docked) or with a modal open the pointer stays free
      // (the station UI; the modal relocks when it closes).
      if (!opts.requirePointerLock || isHeld() || ctx.ui.modal > 0) return
      requestLock()
    },
  })

  // The last input device (dashboard spec §1.5): any key or mouse press is the keyboard; a pad button press, the pad.
  let padLast = false
  const onKeyOrMouse = (): void => {
    padLast = false
  }
  window.addEventListener('keydown', onKeyOrMouse, { capture: true, passive: true })
  window.addEventListener('mousedown', onKeyOrMouse, { capture: true, passive: true })

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pause.pause('hidden')
  })
  window.addEventListener('blur', () => pause.pause('blur'))
  document.addEventListener('pointerlockchange', () => {
    // A hold (docked, loading) or a modal released the lock on purpose: no pause.
    if (opts.requirePointerLock && !hasLock() && !isHeld() && ctx.ui.modal === 0) pause.pause('pointerLock')
  })
  // A refused `ui.relock()` after the last modal closed: pause, as for any other pointer-lock loss.
  document.addEventListener('pointerlockerror', () => {
    if (opts.requirePointerLock && !hasLock() && !isHeld() && ctx.ui.modal === 0) pause.pause('pointerLock')
  })
  window.addEventListener('gamepaddisconnected', () => pause.pause('gamepad'))
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
    ctx.post.setSize(window.innerWidth, window.innerHeight, pixelRatioFor(config, ctx.quality, window.devicePixelRatio))
  })

  // `beforeTicks` hooks, pre-filtered so the hot loop never checks for missing hooks (D-014).
  const before: readonly GameFeature[] = host.features.filter((f) => f.beforeTicks !== undefined)
  const runBeforeTicks = (info: FrameInfo): void => {
    for (let i = 0; i < before.length; i++) {
      const f = before[i]
      try {
        f.beforeTicks?.(ctx, info)
      } catch (err: unknown) {
        throw new Error(`Feature "${f.name}" failed in beforeTicks: ${messageOf(err)}`, { cause: err })
      }
    }
  }

  // Scratch (D-014).
  const control = createPlayerControl()
  const edges = createCombatEdges()
  const shipPos: Vec3 = { x: 0, y: 0, z: 0 }
  const shipRot = new Quaternion()
  const curRot = new Quaternion()
  const info: FrameInfo = { dt: 0, alpha: 1, now: 0, paused: true, held: false }
  let last = -1
  let wasHeld = false

  const frame = (now: number): void => {
    // F3's JS split (PERF PF-1): the frame callback's own time, measured with `performance.now()` (rAF's `now` is
    // the frame's start time, not this callback's).
    const jsStart = performance.now()
    const dt = last < 0 ? 0 : Math.min((now - last) / 1000, 0.25)
    last = now
    const paused = pause.paused
    const p = sim.playerEid

    // Input and edge actions (the pad's tap/hold timing runs on the frame clock).
    pollGamepad(input, bindings, now / 1000)
    const pad = input.pad
    if (pad !== null) for (let i = 0; i < pad.buttons.length; i++) if (pad.buttons[i].pressed) padLast = true
    // Esc/P closes the top modal first; only an unclaimed press pauses (pausing is a no-op while paused).
    if (drainPressed(input, 'pause') && (paused || !consumeEscape(ctx.ui))) pause.pause('user')
    if (drainPressed(input, 'perfOverlay')) togglePerf(perf)
    if (drainPressed(input, 'devPanel')) devPanel?.toggle()

    // Holds: release the pointer once when one begins, take it back when the last one ends.
    const held = isHeld()
    if (held !== wasHeld) {
      resetStepper(stepper)
      if (opts.requirePointerLock) {
        if (held && hasLock()) document.exitPointerLock()
        else if (!held && !paused && !hasLock() && !padLast) requestLock()
      }
      wasHeld = held
    }

    // Fixed sim ticks.
    let simMs = 0
    let ticks = 0
    if (!paused) {
      // With a modal open, or while held (docked: the pad D-pad drives the station UI), the flight edge actions are drained unused.
      const modal = ctx.ui.modal > 0
      if (drainPressed(input, 'toggleFA') && p >= 0 && !modal && !held) Ship.faOn[p] = Ship.faOn[p] === 1 ? 0 : 1
      // M10 P5a7b: hold fire on your crewed batteries; the press is drained but does nothing while weapons.json
      // batteries.playerBatteries is off (review M2).
      if (drainPressed(input, 'turretHoldFire') && p >= 0 && !modal && !held) pressTurretHoldFire(p, ctx.data.weapons)
      if (drainPressed(input, 'toggleCamera') && !modal && !held) rig.mode = rig.mode === 'chase' ? 'cockpit' : 'chase'
      // M2 features see this frame's edges first (tool modes consume `target` and `cyclePrimary`).
      info.dt = dt
      info.now = now
      info.paused = false
      info.held = held
      runBeforeTicks(info)
      drainCombatEdges(input, edges)
      if (!modal) applyCombatEdges(sim, edges)
      recenterJoystick(input.joystick, bindings.mouse.recenterRate, dt)
      if (!held) {
        ticks = consumeTicks(stepper, dt)
        const t0 = performance.now()
        for (let i = 0; i < ticks; i++) {
          const player = sim.playerEid
          if (player >= 0) {
            readPlayerControl(input.keys, input.pad, input.joystick, bindings, control, input.mouseButtons)
            applyControlFilters(ctx.controlFilters, control, ctx.ui.modal > 0)
            writePlayerControl(player, control, hold)
          }
          simControl?.preTick()
          stepSim(sim)
          host.afterTick(ctx)
        }
        simMs = performance.now() - t0
      }
    }
    endFrame(input)

    // Interpolate the player and move the camera rig (float64; the camera itself stays at the origin).
    const alpha = paused || held ? 1 : interpolationAlpha(stepper)
    const player = sim.playerEid
    if (player >= 0 && Life.alive[player] === 1) {
      shipPos.x = PrevPos64.x[player] + (Pos64.x[player] - PrevPos64.x[player]) * alpha
      shipPos.y = PrevPos64.y[player] + (Pos64.y[player] - PrevPos64.y[player]) * alpha
      shipPos.z = PrevPos64.z[player] + (Pos64.z[player] - PrevPos64.z[player]) * alpha
      shipRot.set(PrevRot.x[player], PrevRot.y[player], PrevRot.z[player], PrevRot.w[player])
      curRot.set(Rot.x[player], Rot.y[player], Rot.z[player], Rot.w[player])
      shipRot.slerp(curRot, alpha)
      updateCameraRig(rig, shipPos, shipRot, sim.defs[Ship.defIndex[player]], dt)
    }
    camera.quaternion.copy(rig.rot)

    // Ship views, then the shadow fit around the player (the live view's model radius: the flown hull, M10 P1a1).
    updateShipViews(views, sim, alpha, rig.pos, rig.mode === 'cockpit' ? player : -1)
    const pv = player >= 0 ? views.get(player) : null
    if (pv) ctx.env.setShadowFocus(pv.instance.root.position, pv.radius)
    dust.update(rig.pos)

    // Features, shared clocks, render.
    info.dt = dt
    info.alpha = alpha
    info.now = now
    info.paused = paused
    info.held = held
    const featuresStart = performance.now()
    host.frame(ctx, info)
    const featuresEnd = performance.now()
    const worldDt = paused ? 0 : dt
    ctx.lib.update(worldDt)
    ctx.env.update(worldDt)
    // PERF G1: closes the last frame's GPU timestamp slot; queries run only while F3 is open.
    perf.gpu?.frame(perf.visible)
    const renderStart = performance.now()
    // PERF L-M1: one world-matrix walk per frame, after features, lib and env have moved everything (inside F3's render).
    scene.updateMatrixWorld()
    // SC-6a1: model LODs pick their level once this frame from the view camera (when the preset pins caster LOD).
    beginViewLodFrame(camera)
    ctx.post.render()
    const renderEnd = performance.now()
    // Full-screen overlays (the maps) draw over the finished frame.
    host.overlay(ctx, info)
    const jsEnd = performance.now()

    recordFrame(
      perf,
      dt * 1000,
      simMs,
      ticks,
      jsEnd - jsStart,
      featuresEnd - featuresStart + (jsEnd - renderEnd),
      renderEnd - renderStart,
    )
    if (perf.visible) {
      const c = perf.counts
      c.ships = sim.ships.dense.length
      c.projectiles = sim.projectiles.live
      c.torpedoes = sim.torpedoes.live
      c.particles = particleStats ? particleStats.particles : 0
      c.drops = sim.projectiles.drops
      const l = perf.labels
      l.preset = ctx.quality
      l.backend = backendName(ctx.renderer)
      const strategic = strategicBoot()
      l.strategic = strategic === null ? 'off' : strategic.client.mode === 'worker' ? 'worker' : 'inline'
    }
    updatePerf(perf, ctx.renderer, now)
  }

  return {
    pause,
    run: (): Promise<void> => ctx.renderer.setAnimationLoop(frame),
    stop: (): void => {
      void ctx.renderer.setAnimationLoop(null)
    },
  }
}
